import moment from 'moment';

import * as graphql from '@treams/treams-network';

import { type TalentReviewSnapshotContext } from './TalentReview';
import TalentReviewGrid from './components/TalentReviewGrid';
import TalentReviewTemplateDescription from './components/TalentReviewTemplateDescription';
import { isTalentReviewHistoryDay } from './talentReviewDate';

/**
 * The grid itself plus the template description above it. On an earlier day
 * of the live grid the cells turn grey: the grid is history there.
 * @param props the grid and the template description above it
 * @param props.grid the talent review grid to render
 * @param props.localDate the day being viewed
 * @param props.snapshot set when this is a submitted snapshot rather than the live grid
 * @param props.placements people placed on the grid
 * @param props.dragging true while a person is being dragged
 * @param props.readonly the grid cannot be edited
 * @param props.focusUserID isolates this person and fades everyone else
 * @param props.highlightUserID selection ring without isolating
 * @param props.onSelectUser isolate a placed person; click again to clear
 */
export default function TalentReviewGridContainer(props: {
	grid: graphql.TalentReviewGrid;
	localDate: graphql.Scalars['LocalDate']['input'];
	snapshot?: TalentReviewSnapshotContext;
	placements: graphql.TalentReviewGridPlacement[];
	/** True while a person is being dragged; disables cell instruction popovers. */
	dragging?: boolean;
	readonly?: boolean;
	/** Isolates this person on the grid and fades everyone else. */
	focusUserID?: string;
	/** Shows the selection ring without isolating: sidebar hover, or a drop that opened their card. */
	highlightUserID?: string;
	/** Isolate a placed person; click again to clear. */
	onSelectUser?: (userID: string) => void;
}) {
	const today = moment().format('YYYY-MM-DD');
	const past = isTalentReviewHistoryDay(props.localDate, today, !!props.snapshot);

	return (
		<>
			<TalentReviewTemplateDescription description={props.grid.template.description} />

			<TalentReviewGrid
				key={props.grid.template.id}
				template={props.grid.template}
				placements={props.placements}
				dragging={props.dragging}
				readonly={props.readonly}
				past={past}
				focusUserID={props.focusUserID}
				highlightUserID={props.highlightUserID}
				onSelectUser={props.onSelectUser}
			/>
		</>
	);
}
